import { useState } from 'react';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { Boxes, PlugZap, Save } from 'lucide-react';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { useRascunho } from '@/lib/rascunho';
import { AvisoDeFalha, AvisoDeRegistroMudado } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import {
  chavesDeMudanca,
  definirIcsAfetados,
  definirServicosAfetados,
  listarComponentes,
  listarServicos,
  type IcAfetadoEntrada,
  type ServicoAfetadoEntrada,
} from '@/features/changes/changes.api';
import { CLASSE_DE_CAMPO, Salvo, Secao } from '@/features/changes/componentes';
import {
  assinaturaDeIcs,
  assinaturaDeServicos,
  icsOferecidos,
  servicosOferecidos,
} from '@/features/changes/escopo';
import {
  IMPACTOS_NO_IC,
  ROTULO_DE_IMPACTO,
  type ImpactoNoIc,
  type Mudanca,
} from '@/features/changes/tipos';
import { chaves } from '@/features/tickets/tickets.api';

/**
 * O que a mudança encosta: serviços e itens de configuração.
 *
 * ## As duas listas não são a mesma coisa, e a diferença é o conflito
 *
 * O **item de configuração** é sobre o que a detecção de colisão se resolve:
 * duas mudanças no mesmo servidor em janelas que se sobrepõem é o que o comitê
 * existe para pegar. O **serviço** é sobre o cliente: só a linha marcada com
 * queda entra na Interrupção Projetada de Serviço publicada no portal.
 *
 * Mexer num serviço **sem** derrubá-lo não é interrupção — mas continua sendo
 * escopo de congelamento, porque congelamento é sobre no que a mudança mexe, e
 * não sobre o que ela derruba. Desmarcar a caixa de queda não tira a mudança
 * de dentro de um congelamento do ERP.
 *
 * ## O que esta tela não consegue oferecer
 *
 * Não há rota que **liste o CMDB**. Os itens de configuração alcançáveis são os
 * que constam como componente de algum serviço do catálogo
 * (`GET /catalog/services/:id/components`), e a tela diz isso em vez de fingir
 * que enxerga o inventário inteiro.
 */
export function EscopoDaMudanca({ mudanca }: { mudanca: Mudanca }) {
  return (
    <div className="space-y-6">
      <ServicosAfetados mudanca={mudanca} />
      <IcsAfetados mudanca={mudanca} />
    </div>
  );
}

// ── serviços ───────────────────────────────────────────────────────────────

interface LinhaDeServico {
  serviceId: string;
  causesOutage: boolean;
  downtime: string;
  /**
   * Não há campo para ela nesta tela, e mesmo assim ela viaja no rascunho: a
   * gravação **substitui a lista inteira**, então uma nota escrita por outro
   * caminho seria apagada por um formulário que nem a mostra.
   */
  note: string | null;
}

function ServicosAfetados({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  /**
   * A lista, marcada pela assinatura do que veio do servidor.
   *
   * ★ Não é `mudanca.version`: `POST …/services` substitui a lista inteira e
   * **não** incrementa a versão da mudança, então não há guarda de
   * concorrência nenhuma neste caminho — está relatado. A assinatura detecta a
   * mesma corrida do lado da tela: se a lista que a consulta trouxe mudou
   * depois de o formulário ter sido semeado, alguém mexeu nela, e salvar por
   * cima apagaria o que essa pessoa fez.
   */
  const rascunho = useRascunho<LinhaDeServico[]>(
    () =>
      mudanca.affectedServices.map((linha) => ({
        serviceId: linha.serviceId,
        causesOutage: linha.causesOutage,
        downtime:
          linha.expectedDowntimeMinutes === null ? '' : String(linha.expectedDowntimeMinutes),
        note: linha.note,
      })),
    assinaturaDeServicos(mudanca.affectedServices),
  );
  const linhas = rascunho.valor;

  const catalogo = useQuery({
    queryKey: chavesDeMudanca.servicos,
    queryFn: listarServicos,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const gravar = useMutation({
    mutationFn: () => {
      const entradas: ServicoAfetadoEntrada[] = linhas.map((linha) => ({
        serviceId: linha.serviceId,
        causesOutage: linha.causesOutage,
        expectedDowntimeMinutes:
          linha.downtime.trim() === '' ? null : Number.parseInt(linha.downtime, 10),
        note: linha.note,
      }));
      return definirServicosAfetados(mudanca.ticketId, entradas);
    },
    onMutate: () => setFalha(null),
    // O que esta lista muda é a **Interrupção Projetada de Serviço**, que mora
    // no calendário (`chavesDeMudanca.calendario`) — o servidor emite
    // `ChangeAffectedServicesChanged` dizendo exatamente isso. Invalidar só a
    // raiz de chamados deixaria a PSO publicada mostrando a lista anterior.
    onSuccess: () => {
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.calendarios });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  /**
   * Mexer no formulário apaga o "salvo".
   *
   * Sem isto o aviso ficaria verde ao lado de uma alteração que ainda não foi
   * gravada — que é pior do que não ter aviso nenhum.
   */
  const alterar = (valor: (atual: LinhaDeServico[]) => LinhaDeServico[]) => {
    gravar.reset();
    rascunho.definir(valor);
  };

  // O catálogo é a origem, mas o serviço já declarado que saiu dele continua na
  // lista — escondê-lo faria salvar apagar um vínculo em silêncio.
  const oferecidos = servicosOferecidos(catalogo.data ?? [], mudanca.affectedServices);
  const noCatalogo = new Set((catalogo.data ?? []).map((servico) => servico.id));

  const alternar = (serviceId: string) => {
    alterar((atual) =>
      atual.some((linha) => linha.serviceId === serviceId)
        ? atual.filter((linha) => linha.serviceId !== serviceId)
        : [...atual, { serviceId, causesOutage: false, downtime: '', note: null }],
    );
  };

  const escolhida = (serviceId: string) => linhas.find((linha) => linha.serviceId === serviceId);

  return (
    <Secao
      titulo="Serviços afetados"
      descricao="Só o serviço marcado com queda entra na Interrupção Projetada de Serviço. Mexer sem derrubar não é interrupção — mas continua valendo para o escopo de congelamento."
      acao={
        <div className="flex items-center gap-2">
          <Salvo visivel={gravar.isSuccess} />
          <Button
            size="sm"
            variant="secondary"
            loading={gravar.isPending}
            disabled={rascunho.desatualizado}
            onClick={() => gravar.mutate()}
            icon={<Save className="h-4 w-4" aria-hidden />}
          >
            Salvar serviços
          </Button>
        </div>
      }
    >
      {catalogo.isPending ? <Skeleton className="h-24 w-full" /> : null}
      {catalogo.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(catalogo.error)}
          aoRecarregar={() => void catalogo.refetch()}
        />
      ) : null}

      {catalogo.data?.length === 0 && mudanca.affectedServices.length === 0 ? (
        <EmptyState
          icon={<PlugZap className="h-6 w-6" aria-hidden />}
          title="Nenhum serviço de negócio no catálogo"
          description="Sem catálogo não há Interrupção Projetada de Serviço: a PSO é derivada de janela × serviço afetado, e o serviço precisa existir antes."
        />
      ) : null}

      {oferecidos.length > 0 ? (
      <ul className="divide-y divide-border rounded-md border border-border">
        {oferecidos.map((servico) => {
          const linha = escolhida(servico.id);
          return (
            <li key={servico.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  checked={linha !== undefined}
                  onChange={() => alternar(servico.id)}
                />
                <span className="truncate font-medium">{servico.name}</span>
                <span className="font-mono text-xs text-muted-foreground">{servico.key}</span>
                {!noCatalogo.has(servico.id) ? (
                  <span
                    className="whitespace-nowrap rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning ring-1 ring-inset ring-warning/30"
                    title="Declarado nesta mudança, mas não está mais no catálogo de serviços. Continua na lista para que salvar não apague o vínculo sem ninguém ver."
                  >
                    fora do catálogo
                  </span>
                ) : null}
              </label>

              {linha ? (
                <>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={linha.causesOutage}
                      onChange={(evento) =>
                        alterar((atual) =>
                          atual.map((item) =>
                            item.serviceId === servico.id
                              ? { ...item, causesOutage: evento.target.checked }
                              : item,
                          ),
                        )
                      }
                    />
                    derruba o serviço
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <span className="sr-only">Parada prevista em minutos para {servico.name}</span>
                    <input
                      type="number"
                      min={0}
                      value={linha.downtime}
                      placeholder="min"
                      disabled={!linha.causesOutage}
                      onChange={(evento) =>
                        alterar((atual) =>
                          atual.map((item) =>
                            item.serviceId === servico.id
                              ? { ...item, downtime: evento.target.value }
                              : item,
                          ),
                        )
                      }
                      className={cn(CLASSE_DE_CAMPO, 'w-20 px-2 py-1')}
                    />
                    min de parada
                  </label>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      ) : null}

      {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            rascunho.redefinir();
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });
          }}
        />
      ) : null}
    </Secao>
  );
}

// ── itens de configuração ──────────────────────────────────────────────────

interface LinhaDeIc {
  ciId: string;
  impactKind: ImpactoNoIc;
  causesOutage: boolean;
  /** Viaja sem campo na tela — ver a nota em `LinhaDeServico`. */
  note: string | null;
}

function IcsAfetados({ mudanca }: { mudanca: Mudanca }) {
  const cliente = useQueryClient();
  const [falha, setFalha] = useState<Falha | null>(null);

  // Marcado pela assinatura da lista, e não pela versão da mudança — ver a
  // nota em `ServicosAfetados`.
  const rascunho = useRascunho<LinhaDeIc[]>(
    () =>
      mudanca.affectedCis.map((linha) => ({
        ciId: linha.ciId,
        impactKind: linha.impactKind,
        causesOutage: linha.causesOutage,
        note: linha.note,
      })),
    assinaturaDeIcs(mudanca.affectedCis),
  );
  const linhas = rascunho.valor;

  const catalogo = useQuery({
    queryKey: chavesDeMudanca.servicos,
    queryFn: listarServicos,
    retry: false,
    staleTime: 5 * 60_000,
  });

  // Um pedido por serviço: é o único caminho até o CMDB que a API oferece.
  const componentes = useQueries({
    queries: (catalogo.data ?? []).map((servico) => ({
      queryKey: chavesDeMudanca.componentes(servico.id),
      queryFn: () => listarComponentes(servico.id),
      staleTime: 5 * 60_000,
    })),
  });

  // O IC já declarado na mudança continua na lista mesmo que tenha deixado de
  // ser componente de qualquer serviço: escondê-lo faria salvar a tela apagar
  // um vínculo que ninguém pediu para apagar.
  const disponiveis = icsOferecidos(
    componentes.map((consulta) => consulta.data),
    mudanca.affectedCis,
  );
  const componenteDeAlgumServico = new Set(
    componentes.flatMap((consulta) => (consulta.data ?? []).map((linha) => linha.ci.id)),
  );

  const gravar = useMutation({
    mutationFn: () => {
      const entradas: IcAfetadoEntrada[] = linhas.map((linha) => ({
        ciId: linha.ciId,
        impactKind: linha.impactKind,
        causesOutage: linha.causesOutage,
        note: linha.note,
      }));
      return definirIcsAfetados(mudanca.ticketId, entradas);
    },
    onMutate: () => setFalha(null),
    // Trocar o item de configuração muda a detecção de colisão: o calendário e
    // a ficha precisam recarregar juntos.
    onSuccess: () => {
      rascunho.confirmar();
      void cliente.invalidateQueries({ queryKey: chaves.raiz });
      void cliente.invalidateQueries({ queryKey: chavesDeMudanca.calendarios });
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  /** Mexer no formulário apaga o "salvo" — ver a nota em `ServicosAfetados`. */
  const alterar = (valor: (atual: LinhaDeIc[]) => LinhaDeIc[]) => {
    gravar.reset();
    rascunho.definir(valor);
  };

  const escolhido = (ciId: string) => linhas.find((linha) => linha.ciId === ciId);

  return (
    <Secao
      titulo="Itens de configuração afetados"
      descricao="É sobre esta lista que a colisão se resolve: duas mudanças no mesmo item, em janelas que se sobrepõem, é o conflito que o Comitê de Mudanças existe para pegar."
      acao={
        <div className="flex items-center gap-2">
          <Salvo visivel={gravar.isSuccess} />
          <Button
            size="sm"
            variant="secondary"
            loading={gravar.isPending}
            disabled={rascunho.desatualizado}
            onClick={() => gravar.mutate()}
            icon={<Save className="h-4 w-4" aria-hidden />}
          >
            Salvar itens
          </Button>
        </div>
      }
    >
      <p className="text-xs text-muted-foreground">
        A lista oferece os itens que constam como <strong>componente de algum serviço</strong> do
        catálogo — é o único caminho até a base de configuração que a API expõe hoje. Não há rota
        que liste o inventário inteiro.
      </p>

      {componentes.some((c) => c.isPending) ? <Skeleton className="h-20 w-full" /> : null}

      {disponiveis.length === 0 && !componentes.some((c) => c.isPending) ? (
        <EmptyState
          icon={<Boxes className="h-6 w-6" aria-hidden />}
          title="Nenhum item de configuração alcançável"
          description="Nenhum serviço do catálogo tem componente vinculado. Sem item declarado, a mudança não colide com nenhuma outra — e o calendário não terá o que mostrar ao comitê."
        />
      ) : null}

      {disponiveis.length > 0 ? (
      <ul className="divide-y divide-border rounded-md border border-border">
        {disponiveis.map((ci) => {
          const linha = escolhido(ci.id);
          return (
            <li key={ci.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
              <label className="flex min-w-0 flex-1 items-center gap-2">
                <input
                  type="checkbox"
                  checked={linha !== undefined}
                  onChange={() =>
                    alterar((atual) =>
                      atual.some((item) => item.ciId === ci.id)
                        ? atual.filter((item) => item.ciId !== ci.id)
                        : [
                            ...atual,
                            { ciId: ci.id, impactKind: 'MODIFY', causesOutage: false, note: null },
                          ],
                    )
                  }
                />
                <span className="truncate font-medium">{ci.name}</span>
                <span className="text-xs text-muted-foreground">{ci.ciType}</span>
                {!componenteDeAlgumServico.has(ci.id) ? (
                  <span
                    className="whitespace-nowrap rounded-full bg-warning/15 px-2 py-0.5 text-[11px] font-semibold text-warning ring-1 ring-inset ring-warning/30"
                    title="Declarado nesta mudança, mas não é mais componente de nenhum serviço do catálogo. Continua na lista para que salvar não apague o vínculo sem ninguém ver."
                  >
                    fora do catálogo
                  </span>
                ) : null}
              </label>

              {linha ? (
                <>
                  <label className="flex items-center gap-1.5 text-xs">
                    <span className="sr-only">O que a mudança faz com {ci.name}</span>
                    <select
                      value={linha.impactKind}
                      onChange={(evento) =>
                        alterar((atual) =>
                          atual.map((item) =>
                            item.ciId === ci.id
                              ? { ...item, impactKind: evento.target.value as ImpactoNoIc }
                              : item,
                          ),
                        )
                      }
                      className={cn(CLASSE_DE_CAMPO, 'w-32 px-2 py-1')}
                    >
                      {IMPACTOS_NO_IC.map((valor) => (
                        <option key={valor} value={valor}>
                          {ROTULO_DE_IMPACTO[valor]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex items-center gap-1.5 text-xs">
                    <input
                      type="checkbox"
                      checked={linha.causesOutage}
                      onChange={(evento) =>
                        alterar((atual) =>
                          atual.map((item) =>
                            item.ciId === ci.id
                              ? { ...item, causesOutage: evento.target.checked }
                              : item,
                          ),
                        )
                      }
                    />
                    derruba o item
                  </label>
                </>
              ) : null}
            </li>
          );
        })}
      </ul>
      ) : null}

      {rascunho.desatualizado ? <AvisoDeRegistroMudado aoDescartar={rascunho.redefinir} /> : null}

      {falha ? (
        <AvisoDeFalha
          falha={falha}
          aoRecarregar={() => {
            rascunho.redefinir();
            void cliente.refetchQueries({ queryKey: chaves.item(mudanca.ticketId) });
          }}
        />
      ) : null}
    </Secao>
  );
}
